import type { Language } from '@eformulieren/engine'
import { isOpenAdvies } from '@eformulieren/model'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { AdviceConversation } from './AdvicePanel.js'
import { BusyButton } from './BusyButton.js'
import { type AdviceRequestView, type AdviceTarget, adviceAnchorTitle, targetOf } from './adviceModel.js'
import { type ActionOutcome, formatDay } from './shareModel.js'
import type { Bilingual } from '../language/text.js'

/**
 * Het paneel van de adviseur naast de gedeelde secties (R-184, R-184c, R-139, R-140, ontwerp 7.4a,
 * mockup 05 van advies).
 *
 * Bovenaan de vraag in een eigen kader: van wie (zoals de pod het geeft: "NWO" voor een adviseur
 * buiten NWO, de rol voor een adviseur binnen NWO), wanneer, de termijn en een sprong naar het anker.
 * Daaronder het gesprek als er al meer is dan de vraag, en één antwoordvak met "Advies versturen". Het
 * vak blijft staan zolang het verzoek loopt: een vervolgvraag van de vrager verschijnt in het gesprek,
 * met een melding in een `aria-live=polite`-regio, en de adviseur antwoordt in hetzelfde vak.
 *
 * Heeft NWO zijn belangenverklaring beoordeeld en geen belang vastgesteld (R-184k), dan staat dat
 * oordeel met de toelichting van NWO boven het gesprek; wie oordeelde staat er niet.
 *
 * Geen concept dat vanzelf wordt bewaard: daar is geen opslag voor, en een belofte die niet klopt is
 * erger dan geen belofte. Wat er in het vak staat blijft staan als versturen mislukt.
 */

export interface AdviserPanelProps {
  readonly taal: Language
  readonly verzoek: AdviceRequestView
  /** Wie de vraag stelde, zoals de kijker het mag zien: "NWO", "de toetser". */
  readonly vrager: string
  readonly onAnker: (target: AdviceTarget) => void
  readonly onAntwoord: (tekst: string) => Promise<ActionOutcome>
  /** De focus op de kop zodra het paneel er staat: na de belangenverklaring komt de lezer hier (WCAG 2.4.3). */
  readonly focusBijOpenen?: boolean
  /** Of het paneel een vervolgvraag zelf meldt (standaard ja); zie `meldtZelf` bij `AdvicePanel`. */
  readonly meldtZelf?: boolean
}

const T = {
  kop: { nl: 'Advies', en: 'Advice' },
  vraag: { nl: 'De vraag', en: 'The question' },
  vanWie: { nl: 'Vraag van {w}', en: 'Question from {w}' },
  termijn: { nl: 'antwoord graag voor {d}', en: 'answer requested by {d}' },
  springen: { nl: 'Ga naar {a}', en: 'Go to {a}' },
  over: { nl: 'Over: {a}', en: 'About: {a}' },
  gesprek: { nl: 'Gesprek', en: 'Conversation' },
  uwAdvies: { nl: 'Uw advies', en: 'Your advice' },
  pasNa: { nl: '{w} ziet uw advies pas na versturen.', en: '{w} only sees your advice after you send it.' },
  leeg: { nl: 'Schrijf eerst uw advies.', en: 'Write your advice first.' },
  versturen: { nl: 'Advies versturen', en: 'Send advice' },
  bezig: { nl: 'Bezig met versturen…', en: 'Sending…' },
  verstuurd: { nl: 'Uw advies is verstuurd.', en: 'Your advice has been sent.' },
  vervolg: { nl: 'Stelt {w} een vervolgvraag, dan staat die hier.', en: 'If {w} asks a follow-up question, it appears here.' },
  nieuw: { nl: 'Nieuwe vraag van {w}.', en: 'New question from {w}.' },
  oordeelKop: { nl: 'Het oordeel van NWO over uw belangenverklaring', en: "NWO's judgement on your declaration of interest" },
  oordeelGeen: {
    nl: 'NWO heeft uw belangenverklaring beoordeeld: u hebt volgens NWO geen belang bij deze aanvraag. U kunt het formulier lezen en advies geven.',
    en: 'NWO has reviewed your declaration of interest: according to NWO you have no interest in this application. You can read the form and give advice.',
  },
  oordeelToelichting: { nl: 'Toelichting van NWO: {t}', en: "NWO's explanation: {t}" },
} as const satisfies Readonly<Record<string, Bilingual>>

/** "NWO" blijft zoals het is; een rol als "de toetser" begint de zin met een hoofdletter. */
function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function AdviserPanel({ taal: language, verzoek: request, vrager: asker, onAnker, onAntwoord, focusBijOpenen: focusOnOpen = false, meldtZelf: announces = true }: AdviserPanelProps): ReactNode {
  const headerId = useId()
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (focusOnOpen) heading.current?.focus()
    // Alleen bij het openen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const questionId = useId()
  const judgementId = useId()
  const boxId = useId()
  const helpId = useId()
  const errorId = useId()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState('')
  const [news, setNews] = useState('')

  // Een vervolgvraag sinds de vorige lezing; de eerste lezing meldt niets.
  const known = useRef<ReadonlySet<string> | undefined>(undefined)
  useEffect(() => {
    const before = known.current
    known.current = new Set(request.reacties.map((reaction) => reaction.id))
    if (before === undefined) return
    const said = followUpNews(before, request, asker, language)
    if (said !== undefined) setNews(said)
  }, [request.reacties, language, asker])

  const [question, ...rest] = request.reacties
  const judgement = request.oordelen?.at(-1)
  const target = targetOf(request.anker)
  const title = adviceAnchorTitle(request.anker, language)
  const open = isOpenAdvies(request.toestand)
  const lastIsMine = request.reacties.at(-1)?.rol === 'adviseur'
  const meta = [
    T.vanWie[language].replace('{w}', asker),
    question === undefined ? undefined : formatDay(question.moment, language),
    request.termijn === null ? undefined : T.termijn[language].replace('{d}', formatDay(request.termijn, language)),
  ].filter((part) => part !== undefined)

  return (
    <aside className="ef-advies" aria-labelledby={headerId} lang={language}>
      <div className="ef-advies__kop">
        <h2 id={headerId} ref={heading} tabIndex={-1} className="ef-advies__titel">
          {T.kop[language]}
        </h2>
      </div>
      <section className="ef-adviesvraag" aria-labelledby={questionId}>
        <h3 id={questionId} className="ef-sr-only">
          {T.vraag[language]}
        </h3>
        <p className="ef-adviesvraag__wie">{meta.join(' · ')}</p>
        {question === undefined ? null : <p className="ef-adviesvraag__tekst">{question.tekst}</p>}
        {target === undefined ? (
          <p className="ef-adviesvraag__anker">{T.over[language].replace('{a}', title)}</p>
        ) : (
          <a
            className="ef-adviesvraag__anker"
            href={`#${target.veldId ?? target.sectieId}`}
            onClick={(event) => {
              event.preventDefault()
              onAnker(target)
            }}
          >
            {T.springen[language].replace('{a}', title)}
          </a>
        )}
      </section>
      {/* Wat NWO over zijn verklaring besliste, als het daarover besliste (R-184k): de adviseur leest
          het oordeel en de toelichting, niet wie oordeelde. Hier alleen geen belang, want bij wel belang
          is er geen formulier meer en toont de adviseurpagina het einde. */}
      {judgement?.uitkomst === 'geen-belang' ? (
        <section className="ef-adviesverzoek__belang" aria-labelledby={judgementId}>
          <h3 id={judgementId} className="ef-belangoordeel__kop">
            {T.oordeelKop[language]}
          </h3>
          <p className="ef-belangoordeel__regel">{T.oordeelGeen[language]}</p>
          <p className="ef-belangoordeel__toelichting">{T.oordeelToelichting[language].replace('{t}', judgement.toelichting)}</p>
        </section>
      ) : null}
      <AdviceConversation taal={language} reacties={rest} label={T.gesprek[language]} />
      <p role="status" className="ef-advies__status">
        {status}
      </p>
      {announces ? (
        <div aria-live="polite" className="ef-sr-only">
          {news}
        </div>
      ) : null}
      {open ? (
        <div className="ef-adviesverzoek__reactie">
          {lastIsMine ? <p className="ef-advies__leeg">{T.vervolg[language].replace('{w}', capital(asker))}</p> : null}
          <label htmlFor={boxId} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.uwAdvies[language]}</span>
          </label>
          <textarea
            id={boxId}
            className="ef-invoer ef-adviesverzoek__vak"
            rows={8}
            value={text}
            onChange={(event) => setText(event.target.value)}
            aria-describedby={[helpId, error === undefined ? undefined : errorId].filter((part) => part !== undefined).join(' ')}
            {...(error === undefined ? {} : { 'aria-invalid': true })}
          />
          <p id={helpId} className="ef-veld__hulptekst">
            {T.pasNa[language].replace('{w}', capital(asker))}
          </p>
          {error === undefined ? null : (
            <p id={errorId} role="alert" className="ef-veld__melding--hard">
              {error}
            </p>
          )}
          <BusyButton
            className="ef-actieknop ef-adviesvraag__versturen"
            data-soort="primair"
            busyText={T.bezig[language]}
            onAction={async () => {
              setStatus('')
              const answer = text.trim()
              if (answer === '') {
                setError(T.leeg[language])
                return
              }
              setError(undefined)
              const outcome = await onAntwoord(answer)
              if (outcome.soort === 'gelukt') {
                setText('')
                setStatus(T.verstuurd[language])
                return
              }
              setError(outcome.melding.tekst)
            }}
          >
            {T.versturen[language]}
          </BusyButton>
        </div>
      ) : null}
    </aside>
  )
}

/**
 * De melding bij een vervolgvraag sinds de vorige lezing: "Nieuwe vraag van NWO.", of niets. De
 * adviseurpagina gebruikt haar ook voor de regio van het formulier (`SidePanel.melding`).
 */
export function followUpNews(known: ReadonlySet<string>, request: AdviceRequestView, asker: string, language: Language): string | undefined {
  return request.reacties.some((reaction) => reaction.rol === 'vrager' && !known.has(reaction.id)) ? T.nieuw[language].replace('{w}', asker) : undefined
}
